import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import APIClient from "web_modules/APIClient";
import { InlineLoading } from "carbon-components-react";

import { AppShape, HurdleShape } from "@eave/shared/types/api";
import { loadApp } from "underwriter_dashboard/actions";
import HurdleView from "underwriter_dashboard/shared/HurdleView";

import { VALoanAppPayload, LoanAppUpdateVAValuesPayload } from "@eave/shared/types/api/va-loans";
import Toast, { useToast } from "web_modules/shared/Toast";
import { getVALoansPageData } from "./data-helpers";

import VeteranAddendumForm from "./forms/VeteranAddendumForm";
import GeneralVAForm from "./forms/GeneralVAForm";
import { initialValues as addendumInitialValues } from "./forms/VeteranAddendumForm/form-values";
import PageSection from "./PageSection";

import { VAPageSection } from "./types";

interface Props {
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
  loadApp: any;
}

const VALoansEditor = (props: Props) => {
  const { app, hurdle, onHide, loadApp } = props;

  const [isLoading, setIsLoading] = useState(false);

  // Toast notification hooks
  const { toastHooks, notifyFunctions } = useToast();
  const { notifyError } = notifyFunctions;

  const [pageData, setPageData] = useState(null);

  const loadData = async () => {
    const data = await getVALoansPageData(app);
    setPageData(data);
    setIsLoading(false);
  };

  // Load initial data
  useEffect(() => {
    // Load the data
    loadData();
  }, []);

  // Save updates to the va_loans_application table
  const saveVALoanAppUpdates = (data: VALoanAppPayload) => {
    setIsLoading(true);
    APIClient.post(`/uw/loan_applications/${app.id}/va_loan_application`, data)
      .then(() => {
        loadApp(app.id);
        loadData();
      })
      .catch(err => {
        notifyError(`An error occurred: ${err?.meta?.error_detail}`);
      });
  };

  // Save updates to the loans_application table and the loan_application_borrowers table
  const saveLoanAppUpdates = (data: LoanAppUpdateVAValuesPayload) => {
    setIsLoading(true);

    const promises = [];
    Object.keys(app.borrowers).forEach(key => {
      const payload = {
        borrower: {
          ...app.borrowers[`${key}`],
          is_veteran: data[`borrower${key}_is_veteran`],
          use_va_entitlement: data[`borrower${key}_use_va_entitlement`],
        },
      };
      promises.push(
        APIClient.put(
          `/uw/loan_applications/${app.id}/loan_application_borrowers/entity/${key}`,
          payload,
        ),
      );
    });

    const loanAppPayload = {
      borrower1_is_veteran: data.borrower1_is_veteran,
      borrower2_is_veteran: data.borrower2_is_veteran,
      borrower1_use_va_entitlement: data.borrower1_use_va_entitlement,
      borrower2_use_va_entitlement: data.borrower2_use_va_entitlement,
      is_va_loan_application_borrower_value: data.is_va_loan_application_borrower_value,
    };
    promises.push(APIClient.put(`/loan_applications/${app.id}`, loanAppPayload));

    Promise.all(promises)
      .then(() => loadApp(app.id))
      .catch(err => {
        notifyError(`There was an error updating VA. Please contact engineering.`);
        throw err;
      });
  };

  if (!pageData || isLoading) {
    return <InlineLoading description="Loading..." status="active" />;
  }

  const { BorrowerInfo, LoanInfo, LenderSponsorAgentInfo } = VAPageSection;

  return (
    <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
      <div className="dashboard-container">
        <Toast {...toastHooks} />
        <div style={{ padding: "32px" }}>
          <VeteranAddendumForm
            initialValues={pageData.va_loan_application_id ? pageData : addendumInitialValues}
            onSubmit={saveVALoanAppUpdates}
          />
          <GeneralVAForm
            initialValues={pageData}
            hasCoborrower={app.has_borrower2}
            onSubmit={saveLoanAppUpdates}
          />
          <PageSection section={BorrowerInfo} data={pageData} />
          <PageSection section={LoanInfo} data={pageData} />
          <PageSection section={LenderSponsorAgentInfo} data={pageData} />
        </div>
      </div>
    </HurdleView>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  loadApp: (appId: number) => loadApp(dispatch, appId),
});

export default connect(null, mapDispatchToProps)(VALoansEditor);
